import React from 'react';
import { Button, Message } from '@bedrock/components';
import { Download } from '@bedrock/icons-react';
import { supplierDefs, PartnershipRequestInvoker } from '@purchase/supplier';
import { downloadFile } from '@mpc/common/src/utils/download';
import { formatFileSize } from '@mpc/common/src/utils';

type LegacyStorageFields = { external_id?: string; _id?: number };

const FILE_ICON = (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
    <path d="M10.2897 1.00024H4.20791C2.98843 1.00024 2 1.98942 2 3.20949V12.791C2 14.0112 2.98858 15.0002 4.20791 15.0002H11.7921C13.0116 15.0002 14 14.0111 14 12.791V4.71264C14 4.21508 13.8025 3.738 13.4509 3.38628L11.6152 1.54955C11.2637 1.19783 10.7868 1.00024 10.2897 1.00024Z" fill="var(--n50,#9DAAB3)"/>
    <path d="M4.48751 11.5223C3.8375 10.8854 3.8375 9.85279 4.48751 9.21588L8.12535 5.65138C9.01173 4.78287 10.4488 4.78287 11.3352 5.65138C12.2216 6.51989 12.2216 7.92802 11.3352 8.79653L9.88424 10.2182C9.70955 10.3894 9.43003 10.3894 9.25534 10.2182C9.07554 10.0421 9.07554 9.75258 9.25534 9.57641L10.6932 8.1675C11.2251 7.64639 11.2251 6.80152 10.6932 6.28041C10.1614 5.75931 9.29915 5.75931 8.76733 6.28041L5.12948 9.84491C4.83402 10.1344 4.83402 10.6038 5.12948 10.8933C5.42494 11.1828 5.90398 11.1828 6.19944 10.8933L9.67364 7.48913C9.73273 7.43123 9.73273 7.33736 9.67364 7.27946C9.61455 7.22156 9.51874 7.22156 9.45965 7.27946L7.15271 9.53988C6.97802 9.71105 6.6985 9.71105 6.52381 9.53988C6.34401 9.36371 6.34401 9.07422 6.52381 8.89804L8.81768 6.65043C9.23132 6.24512 9.90197 6.24512 10.3156 6.65043C10.7293 7.05573 10.7293 7.71286 10.3156 8.11816L6.84141 11.5223C6.1914 12.1592 5.13752 12.1592 4.48751 11.5223Z" fill="white"/>
  </svg>
);

const AttachmentRow = ({ file }: { file: supplierDefs.StorageObject }) => {
  // 事故附件（OA 文件引用）常只有 externalId 无 name，给可识别占位。
  // 兼容 genapi 未转 snake_case 的情况（external_id / _id）。
  const legacy = file as unknown as LegacyStorageFields;
  const externalId = file?.externalId ?? legacy?.external_id;
  const fileId = file?.id ?? legacy?._id;
  const displayName = file?.name || (externalId ? `附件${String(externalId).slice(-6)}` : '--');
  const canPreview = fileId != null;

  const handlePreview = async () => {
    if (!fileId) return;
    try {
      const res = await PartnershipRequestInvoker.getInstance().partnerShipOpenApiAttachmentPreviewUrl({
        fileId,
      });
      if (res?.data) {
        window.open(res.data, '_blank');
      }
    } catch {
      Message.error({ content: '预览失败' });
    }
  };

  const handleDownload = async () => {
    if (!fileId) {
      Message.warn({ content: '附件暂不支持下载' });
      return;
    }
    try {
      const res = await PartnershipRequestInvoker.getInstance().partnerShipOpenApiAttachmentDownloadUrl({
        fileId,
      });
      if (res?.data) {
        downloadFile(res.data, file.name);
      } else {
        Message.warn({ content: '下载失败' });
      }
    } catch {
      Message.error({ content: '下载失败' });
    }
  };

  return (
    <div className="flex items-center gap-2 h-8">
      <span className="inline-flex items-center text-[#9DAAB3] shrink-0">
        {FILE_ICON}
      </span>
      {canPreview ? (
        <span
          className="text-color-1 cursor-pointer hover:text-[#3366FF] truncate max-w-[300px]"
          title={displayName}
          onClick={handlePreview}
        >
          {displayName}
        </span>
      ) : (
        <span
          className="text-color-1 truncate max-w-[300px]"
          title={displayName}
        >
          {displayName}
        </span>
      )}
      {file.size != null && (
        <span className="text-color-4 text-xs whitespace-nowrap">({formatFileSize(file.size)})</span>
      )}
      {fileId && (
        <Button type="text-subtle" size="xSmall" icon={<Download />} onClick={handleDownload} />
      )}
    </div>
  );
};

/**
 * 附件展示（脱离表单）：文件卡片样式（图标 + 可预览文件名 + 文件大小 + 下载按钮）。
 * 不走 FormItemBaseFile（其内部 ProUpload 依赖表单 ChangeDataProvider，无表单上下文会崩）。
 */
const AttachmentItem = ({
  value,
}: {
  value?: supplierDefs.StorageObject | supplierDefs.StorageObject[];
}) => {
  const list = value ? (Array.isArray(value) ? value : [value]) : [];
  if (list.length === 0) {
    return <span>--</span>;
  }
  return (
    <div className="flex flex-col gap-2">
      {list.map((file, idx) => {
        const legacy = file as unknown as LegacyStorageFields;
        const externalId = file?.externalId ?? legacy?.external_id;
        return (
          <AttachmentRow
            key={file.id ?? externalId ?? file.name ?? idx}
            file={file}
          />
        );
      })}
    </div>
  );
};

export default AttachmentItem;
